import { useGetCartQuery } from "@/api/AppApi/services/cart";
import {
  AddOnTypes,
  IProductAddOn,
} from "@/api/AppApi/services/product/interfaces/api-product-details-interface";
import OneCardOption from "@/app/components/common-addons-builder/common-addons-fields/hook-card-options/OneCardOption";
import OptionsDropdown from "@/app/components/common-addons-builder/common-addons-fields/hook-dropdown/options-dropdown/OptionsDropdown";
import CheckboxOption from "@/app/components/common-filter-builder/common-filters-components/CheckboxOption";
import OptionsCarousel from "@/app/components/common-filter-builder/common-filters-components/OptionsCarousel";
import {
  changeAddonQuantity,
  isSelectedAddon,
  toggleAddonWithQuantity,
} from "@/app/components/common-filter-builder/common-filters-components/toggleOption";
import { compareAddOnsArrays } from "@/hooks/product-has-attrs/product-has-attrs-helpers/functions";
import {
  IAddons,
  IAddOnWithQuantity,
} from "@/hooks/product-has-attrs/product-has-attrs-helpers/interfaces";

interface AddOnProps {
  addOn: IProductAddOn;
  state: IAddOnWithQuantity[];
  setState: (state: IAddOnWithQuantity[]) => void;
  detailsCountaineMaxWidth?: number;
  disablechangeAddon?: boolean;
  selectedAddOns: IAddons | undefined;
  productId?: number;
}

const ProductAddonsBuilder: React.FC<AddOnProps> = ({
  addOn,
  state,
  setState,
  detailsCountaineMaxWidth,
  disablechangeAddon,
  selectedAddOns,
  productId,
}) => {
  const getCart = useGetCartQuery();
  const cartItems = getCart?.data?.result?.items;

  const handleOption = (key: number, quantity: number) => {
    const ToggleOption = toggleAddonWithQuantity(
      state,
      key,
      addOn.configurations.is_multi_select,
      quantity,
    );
    const newSelectedAddOns = {
      ...selectedAddOns,
      [addOn.configurations.key]: ToggleOption,
    };
    const addOns: IAddOnWithQuantity[][] = [];
    for (let key in newSelectedAddOns) {
      newSelectedAddOns[key] && addOns.push(newSelectedAddOns[key]);
    }
    const cartItemFromAddOns = (cartItems ?? [])?.find(
      (item) =>
        item.inner_id === productId &&
        compareAddOnsArrays(item.addons, addOns.flat()),
    );
    let newToggleOption = [...ToggleOption];
    if (!!cartItemFromAddOns) {
      newToggleOption = ToggleOption.map((option) =>
        key != option.id
          ? { ...option }
          : {
              ...option,
              quantity:
                cartItemFromAddOns.addons.find((addOn) => addOn.id === key)
                  ?.quantity ?? option.quantity,
            },
      );
    }
    setState(newToggleOption);
  };
  const handleChangQuantity = (key: number, quantity: number) => {
    setState(changeAddonQuantity(state, key, quantity));
  };
  const getQuantityContor = (
    id: number,
    maxQuantity: number,
    minQuantity: number,
    enable_quantity_selection: boolean,
  ) => {
    if (!enable_quantity_selection) return;
    const addOn = state?.find((addon) => addon.id === id);
    return {
      quantity: addOn?.quantity ?? minQuantity,
      setQuantity: (quantity: number) =>
        quantity > 0
          ? handleChangQuantity(id, quantity)
          : handleOption(id, minQuantity),
      plusDisabled: (addOn?.quantity ?? 1) >= maxQuantity,
      minQuantity,
    };
  };

  const is_maximum_selected_addons =
    state?.length >= addOn.configurations.maximum_selected_addons;

  const selectedQTYFromThisAddOn = (state ?? [])?.reduce(
    (sum, addOn) => sum + addOn.quantity,
    0,
  );
  const configurations_max_quantity =
    addOn.configurations.max_quantity ?? Infinity;

  const isThisAddOnsMaxQTY =
    selectedQTYFromThisAddOn >= configurations_max_quantity;

  const disabledAddOn = is_maximum_selected_addons || isThisAddOnsMaxQTY;

  switch (addOn.configurations.type) {
    case AddOnTypes.checkbox:
      return (
        <ul
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            margin: 0,
            padding: 0,
            listStyleType: "none",
          }}
        >
          {addOn.data?.map((op, key) => {
            const MaxQuntity = Math.min(
              op.max_quantity ?? op.quantity ?? Infinity,
              configurations_max_quantity -
                selectedQTYFromThisAddOn +
                (state?.find((addon) => addon.id === op.id)?.quantity ?? 0),
            );
            const MinQuntity = op.min_quantity ?? 1;
            return (
              <CheckboxOption
                key={key}
                disabled={
                  disabledAddOn ||
                  selectedQTYFromThisAddOn + MinQuntity >
                    configurations_max_quantity
                }
                disableRemoveAddon={disablechangeAddon}
                is_multi_select={addOn.configurations.is_multi_select}
                checked={isSelectedAddon(state, op.id)}
                toggleOption={() => handleOption(op.id, MinQuntity)}
                label={op.name}
                value={op.price}
                quantityContor={getQuantityContor(
                  op.id,
                  MaxQuntity,
                  MinQuntity,
                  op.enable_quantity_selection,
                )}
                outOfStock={op.quantity! < 1}
              />
            );
          })}
        </ul>
      );
    case AddOnTypes.dropdown:
      return (
        <OptionsDropdown
          title={state
            ?.map(
              (selectedAddon) =>
                addOn.data[
                  addOn.data.findIndex((o) => o.id === selectedAddon.id)
                ]?.name,
            )
            .join(", ")}
        >
          {addOn.data?.map((op, key) => {
            const MaxQuntity = Math.min(
              op.max_quantity ?? op.quantity ?? Infinity,
              configurations_max_quantity -
                selectedQTYFromThisAddOn +
                (state?.find((addon) => addon.id === op.id)?.quantity ?? 0),
            );
            const MinQuntity = op.min_quantity ?? 1;
            return (
              <CheckboxOption
                key={key}
                disabled={
                  disabledAddOn ||
                  selectedQTYFromThisAddOn + MinQuntity >
                    configurations_max_quantity
                }
                disableRemoveAddon={disablechangeAddon}
                is_multi_select={addOn.configurations.is_multi_select}
                checked={isSelectedAddon(state, op.id)}
                toggleOption={() => handleOption(op.id, MinQuntity)}
                label={op.name}
                value={op.price}
                quantityContor={getQuantityContor(
                  op.id,
                  MaxQuntity,
                  MinQuntity,
                  op.enable_quantity_selection,
                )}
                outOfStock={op.quantity! < 1}
              />
            );
          })}
        </OptionsDropdown>
      );
    case AddOnTypes.card:
      return (
        <OptionsCarousel
          width={156}
          spacing={8}
          sliderMaxWidth={detailsCountaineMaxWidth}
        >
          {addOn.data?.map((op, key) => {
            const MaxQuntity = Math.min(
              op.max_quantity ?? op.quantity ?? Infinity,
              configurations_max_quantity -
                selectedQTYFromThisAddOn +
                (state?.find((addon) => addon.id === op.id)?.quantity ?? 0),
            );
            const MinQuntity = op.min_quantity ?? 1;
            return (
              <OneCardOption
                key={key}
                disabled={
                  disabledAddOn ||
                  selectedQTYFromThisAddOn + MinQuntity >
                    configurations_max_quantity
                }
                disableRemoveAddon={disablechangeAddon}
                is_multi_select={addOn.configurations.is_multi_select}
                label={op.name}
                url={op.image ?? ""}
                selected={isSelectedAddon(state, op.id)}
                toggleOption={() => handleOption(op.id, MinQuntity)}
                value={op.price}
                quantityContor={getQuantityContor(
                  op.id,
                  MaxQuntity,
                  MinQuntity,
                  op.enable_quantity_selection,
                )}
                outOfStock={op.quantity! < 1}
              />
            );
          })}
        </OptionsCarousel>
      );
    default:
      return null;
  }
};

export default ProductAddonsBuilder;
